const CHAT_LIST_KEYBOARD_NAV_CONVERSATIONS_SELECTOR =
  'div.conversations-list div.conversation';
const CHAT_LIST_KEYBOARD_NAV_ACTIVE_SELECTOR =
  'div.conversations-list div.conversation.active';

export const resolveChatListKeyboardNavigationTargetIndex = ({
  activeConversationIndex = -1,
  direction = 'next',
  lastConversationIndex = -1,
} = {}) => {
  if (lastConversationIndex < 0) {
    return null;
  }
  if (direction === 'previous') {
    if (activeConversationIndex === -1) {
      return 0;
    }
    if (activeConversationIndex >= 1) {
      return activeConversationIndex - 1;
    }
    return null;
  }
  if (activeConversationIndex === -1) {
    return lastConversationIndex;
  }
  if (activeConversationIndex < lastConversationIndex) {
    return activeConversationIndex + 1;
  }
  return null;
};

export const extractChatListResizeObserverWidth = (entry = null) => {
  const width = entry?.contentRect?.width;
  return typeof width === 'number' ? width : 0;
};

export const resolveChatListContainerWidthUpdate = ({
  currentWidth = 0,
  nextWidth = 0,
} = {}) => ({
  shouldUpdate: nextWidth !== currentWidth,
  width: nextWidth,
});

export const runChatListWindowResize = ({
  applyWidthUpdate = null,
  getContainer = null,
  nextTick = null,
} = {}) => {
  if (
    typeof applyWidthUpdate !== 'function' ||
    typeof nextTick !== 'function'
  ) {
    return { status: 'invalid' };
  }

  nextTick(() => {
    const container =
      typeof getContainer === 'function' ? getContainer() : null;
    if (container) {
      applyWidthUpdate(container.clientWidth);
    }
  });

  return { status: 'scheduled' };
};

export const runChatListResizeObserverInitialize = ({
  addWindowResizeListener = null,
  applyWidthUpdate = null,
  createResizeObserver = null,
  extractWidth = extractChatListResizeObserverWidth,
  getContainer = null,
  handleWindowResize = null,
  nextTick = null,
  observeResizeObserver = null,
  setResizeObserver = null,
} = {}) => {
  if (
    typeof applyWidthUpdate !== 'function' ||
    typeof createResizeObserver !== 'function' ||
    typeof setResizeObserver !== 'function'
  ) {
    return { status: 'invalid', observer: null };
  }

  const observer = createResizeObserver((entries = []) => {
    if (!Array.isArray(entries)) return;

    entries.forEach((entry) => {
      applyWidthUpdate(
        typeof extractWidth === 'function' ? extractWidth(entry) : 0
      );
    });
  });

  setResizeObserver(observer);

  if (!observer) {
    if (
      typeof addWindowResizeListener === 'function' &&
      typeof handleWindowResize === 'function'
    ) {
      addWindowResizeListener('resize', handleWindowResize);
      handleWindowResize();
    }
    return { status: 'fallback', observer: null };
  }

  if (typeof nextTick !== 'function') {
    return { status: 'observer', observer };
  }

  nextTick(() => {
    const container =
      typeof getContainer === 'function' ? getContainer() : null;
    if (!container) return;

    if (typeof observeResizeObserver === 'function') {
      observeResizeObserver(observer, container);
    }
    applyWidthUpdate(container.clientWidth);
  });

  return { status: 'observer', observer };
};

export const runChatListResizeObserverCleanup = ({
  disconnectResizeObserver = null,
  handleWindowResize = null,
  removeWindowResizeListener = null,
  resizeObserver = null,
  setResizeObserver = null,
} = {}) => {
  if (resizeObserver) {
    if (typeof disconnectResizeObserver === 'function') {
      disconnectResizeObserver(resizeObserver);
    }
    if (typeof setResizeObserver === 'function') {
      setResizeObserver(null);
    }
  }

  if (
    typeof removeWindowResizeListener === 'function' &&
    typeof handleWindowResize === 'function'
  ) {
    removeWindowResizeListener('resize', handleWindowResize);
  }

  return { status: 'cleaned' };
};

export const buildChatListKeyboardNavigationParams = (container = null) => {
  if (
    !container ||
    typeof container.querySelectorAll !== 'function' ||
    typeof container.querySelector !== 'function'
  ) {
    return {
      allConversations: [],
      activeConversation: null,
      activeConversationIndex: -1,
      lastConversationIndex: -1,
    };
  }
  const allConversations = container.querySelectorAll(
    CHAT_LIST_KEYBOARD_NAV_CONVERSATIONS_SELECTOR
  );
  const activeConversation = container.querySelector(
    CHAT_LIST_KEYBOARD_NAV_ACTIVE_SELECTOR
  );
  const conversationsArray = Array.from(allConversations);
  return {
    allConversations,
    activeConversation,
    activeConversationIndex: conversationsArray.indexOf(activeConversation),
    lastConversationIndex: conversationsArray.length - 1,
  };
};

export const buildChatListKeyboardEvents = ({
  onNextConversation = null,
  onPreviousConversation = null,
} = {}) => ({
  'Alt+KeyJ': {
    action:
      typeof onPreviousConversation === 'function'
        ? onPreviousConversation
        : () => {},
    allowOnFocusedInput: true,
  },
  'Alt+KeyK': {
    action:
      typeof onNextConversation === 'function' ? onNextConversation : () => {},
    allowOnFocusedInput: true,
  },
});

export const runChatListKeyboardNavigation = ({
  buildNavigationParams = buildChatListKeyboardNavigationParams,
  container = null,
  direction = 'next',
  resolveTargetIndex = resolveChatListKeyboardNavigationTargetIndex,
} = {}) => {
  if (
    typeof buildNavigationParams !== 'function' ||
    typeof resolveTargetIndex !== 'function'
  ) {
    return { status: 'invalid', targetIndex: null };
  }

  const { allConversations, activeConversationIndex, lastConversationIndex } =
    buildNavigationParams(container);
  const targetIndex = resolveTargetIndex({
    activeConversationIndex,
    direction,
    lastConversationIndex,
  });
  const targetConversation =
    targetIndex === null ? null : allConversations?.[targetIndex];

  if (typeof targetConversation?.click !== 'function') {
    return { status: 'skipped', targetIndex };
  }

  targetConversation.click();
  return { status: 'clicked', targetIndex };
};
